import { slugify } from "@/utils/slugify";

export interface CertificateData {
  attendeeName: string;
  courseName: string;
  completionDate: string;
}

export const CERTIFICATE_SIGNER = "Manny Rodriguez, CertainPath CEO";

/**
 * Enrollment names arrive with stray whitespace (trailing spaces and doubled
 * inner spaces are both present in live data), which would print verbatim on
 * the certificate. Collapses any whitespace run to a single space and trims.
 */
export function normalizeDisplayName(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

export function signerScriptName(signer: string): string {
  return signer.split(",")[0].trim();
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseIsoDateParts(value: string): Date | null {
  const match = ISO_DATE.exec(value.trim());
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, monthIndex, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== monthIndex ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

export function formatCompletionDate(sessionDates: string): string {
  if (!sessionDates) {
    return "";
  }

  const parts = sessionDates
    .split("-")
    .map((part) => part.trim())
    .filter(Boolean);

  // "YYYY-MM-DD" is 3 parts, "YYYY-MM-DD - YYYY-MM-DD" is 6. Take the last 3.
  if (parts.length < 3) {
    return "";
  }

  const endDate = parseIsoDateParts(parts.slice(-3).join("-"));
  if (!endDate) {
    return "";
  }

  return endDate.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function certificateFileName(data: CertificateData): string {
  const segments = [data.attendeeName, data.courseName, data.completionDate]
    .map((segment) => slugify(segment ?? ""))
    .filter(Boolean);

  const name = [...segments, "certificate"]
    .join("-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");

  return `${name}.pdf`;
}
